import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiSave, FiEye, FiEyeOff, FiCheck } = FiIcons;

const SettingsModal = ({ onClose, onApiKeyChange, onAiSettingsChange, currentApiKey = '', currentAiSettings = {} }) => {
  const [apiKey, setApiKey] = useState(currentApiKey || '');
  const [showApiKey, setShowApiKey] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({
    company_name: '',
    owner_name: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    phone: '',
    email: '',
    website: ''
  });
  const [aiSettings, setAiSettings] = useState({
    provider: currentAiSettings.provider || 'openai',
    openai_model: currentAiSettings.openai_model || 'gpt-3.5-turbo',
    straico_api_key: currentAiSettings.straico_api_key || '',
    straico_model: currentAiSettings.straico_model || 'YOUR_MODEL_IDENTIFIER'
  });
  const [showStraicoApiKey, setShowStraicoApiKey] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      // Load OpenAI API Key
      const { data: apiKeyData, error: apiKeyError } = await supabase
        .from('app_settings')
        .select('setting_value')
        .eq('setting_key', 'openai_api_key')
        .single();
      
      if (apiKeyData && !apiKeyError) {
        setApiKey(apiKeyData.setting_value || '');
      }

      // Load all AI provider settings
      const { data: aiProviderSettings, error: aiProviderError } = await supabase
        .from('app_settings')
        .select('setting_key, setting_value');
      
      if (aiProviderSettings && !aiProviderError) {
        const settings = {
          provider: 'openai',
          openai_model: 'gpt-3.5-turbo',
          straico_api_key: '',
          straico_model: 'YOUR_MODEL_IDENTIFIER'
        };
        
        aiProviderSettings.forEach(setting => {
          if (setting.setting_key === 'ai_provider') settings.provider = setting.setting_value;
          if (setting.setting_key === 'openai_model') settings.openai_model = setting.setting_value;
          if (setting.setting_key === 'straico_api_key') settings.straico_api_key = setting.setting_value;
          if (setting.setting_key === 'straico_model') settings.straico_model = setting.setting_value;
        });
        
        console.log("Loaded AI settings:", settings);
        setAiSettings(settings);
      }

      // Load company information
      const { data: companyData, error: companyError } = await supabase
        .from('company_info')
        .select('*')
        .limit(1)
        .single();
      
      if (companyData && !companyError) {
        setCompanyInfo(companyData);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyInfoChange = (e) => {
    const { name, value } = e.target;
    setCompanyInfo(prev => ({ ...prev, [name]: value }));
  };

  const handleAiSettingsChange = (e) => {
    const { name, value } = e.target;
    setAiSettings(prev => ({ ...prev, [name]: value }));
  };

  const saveSettings = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError('');
    
    try {
      console.log("Saving settings with AI settings:", aiSettings);
      
      // Check if settings exist first to avoid duplicate key errors
      const { data: existingSettings, error: checkError } = await supabase
        .from('app_settings')
        .select('setting_key');
        
      if (checkError) throw new Error(`Failed to check existing settings: ${checkError.message}`);
      
      const existingKeys = existingSettings.map(s => s.setting_key);
      
      // Helper function to upsert a setting with proper error handling
      const upsertSetting = async (key, value) => {
        try {
          if (existingKeys.includes(key)) {
            // Update existing setting
            const { error } = await supabase
              .from('app_settings')
              .update({
                setting_value: value,
                updated_at: new Date().toISOString()
              })
              .eq('setting_key', key);
              
            if (error) throw error;
          } else {
            // Insert new setting
            const { error } = await supabase
              .from('app_settings')
              .insert({
                setting_key: key,
                setting_value: value,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              });
              
            if (error) throw error;
          }
          return true;
        } catch (error) {
          console.error(`Error saving setting ${key}:`, error);
          throw error;
        }
      };
      
      // Save all settings using the helper function
      await upsertSetting('openai_api_key', apiKey);
      await upsertSetting('ai_provider', aiSettings.provider);
      await upsertSetting('openai_model', aiSettings.openai_model);
      await upsertSetting('straico_api_key', aiSettings.straico_api_key);
      await upsertSetting('straico_model', aiSettings.straico_model);
      
      // Check if company info record exists
      const { data: existingCompany, error: checkCompanyError } = await supabase
        .from('company_info')
        .select('id')
        .limit(1);
      
      if (checkCompanyError) {
        console.error("Error checking company info:", checkCompanyError);
        throw new Error(`Failed to check company info: ${checkCompanyError.message}`);
      }
      
      // Save company information
      if (existingCompany && existingCompany.length > 0) {
        // Update existing record
        const { error: updateError } = await supabase
          .from('company_info')
          .update({
            ...companyInfo,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingCompany[0].id);
          
        if (updateError) {
          throw new Error(`Failed to update company info: ${updateError.message}`);
        }
      } else {
        // Insert new record
        const { error: insertError } = await supabase
          .from('company_info')
          .insert([{
            ...companyInfo,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }]);
          
        if (insertError) {
          throw new Error(`Failed to insert company info: ${insertError.message}`);
        }
      }

      // Verify that Straico settings were saved correctly
      const { data: verifyData, error: verifyError } = await supabase
        .from('app_settings')
        .select('setting_value')
        .eq('setting_key', 'straico_api_key')
        .single();
        
      if (verifyError) {
        console.error("Error verifying Straico API key:", verifyError);
      } else {
        console.log("Verified Straico API Key:", verifyData);
        if (verifyData.setting_value !== aiSettings.straico_api_key) {
          console.warn("Straico API key mismatch! Expected:", aiSettings.straico_api_key, "Got:", verifyData.setting_value);
        }
      }

      // Update parent component state
      onApiKeyChange(apiKey);
      onAiSettingsChange(aiSettings);
      
      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveError(error.message || 'Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const openAIModels = [
    'gpt-3.5-turbo',
    'gpt-3.5-turbo-16k',
    'gpt-4',
    'gpt-4-32k',
    'gpt-4-turbo'
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiX} className="h-6 w-6" />
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-electric-600"></div>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Company Information */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="company_name"
                      value={companyInfo.company_name}
                      onChange={handleCompanyInfoChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Owner/Manager Name
                    </label>
                    <input
                      type="text"
                      name="owner_name"
                      value={companyInfo.owner_name}
                      onChange={handleCompanyInfoChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={companyInfo.address}
                      onChange={handleCompanyInfoChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={companyInfo.city}
                        onChange={handleCompanyInfoChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State
                      </label>
                      <input
                        type="text"
                        name="state"
                        value={companyInfo.state}
                        onChange={handleCompanyInfoChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Postal Code
                    </label>
                    <input
                      type="text"
                      name="postal_code"
                      value={companyInfo.postal_code}
                      onChange={handleCompanyInfoChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="text"
                      name="phone"
                      value={companyInfo.phone}
                      onChange={handleCompanyInfoChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={companyInfo.email}
                      onChange={handleCompanyInfoChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Website
                    </label>
                    <input
                      type="text"
                      name="website"
                      value={companyInfo.website}
                      onChange={handleCompanyInfoChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                      placeholder="https://example.com"
                    />
                  </div>
                </div>
              </div>

              {/* AI Settings */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Settings</h3>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    AI Provider
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div 
                      className={`border rounded-lg p-4 cursor-pointer ${aiSettings.provider === 'openai' ? 'border-electric-500 bg-electric-50' : 'border-gray-200'}`}
                      onClick={() => setAiSettings(prev => ({ ...prev, provider: 'openai' }))}
                    >
                      <div className="flex items-center">
                        <div className={`h-4 w-4 rounded-full mr-2 ${aiSettings.provider === 'openai' ? 'bg-electric-500' : 'bg-gray-200'}`}></div>
                        <h4 className="font-medium">OpenAI</h4>
                      </div>
                    </div>
                    <div 
                      className={`border rounded-lg p-4 cursor-pointer ${aiSettings.provider === 'straico' ? 'border-electric-500 bg-electric-50' : 'border-gray-200'}`}
                      onClick={() => setAiSettings(prev => ({ ...prev, provider: 'straico' }))}
                    >
                      <div className="flex items-center">
                        <div className={`h-4 w-4 rounded-full mr-2 ${aiSettings.provider === 'straico' ? 'bg-electric-500' : 'bg-gray-200'}`}></div>
                        <h4 className="font-medium">Straico</h4>
                      </div>
                    </div>
                  </div>
                </div>

                {/* OpenAI settings - always show regardless of provider */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      OpenAI API Key
                    </label>
                    <div className="relative">
                      <input
                        type={showApiKey ? 'text' : 'password'}
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                        placeholder="sk-..."
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <SafeIcon
                          icon={showApiKey ? FiEyeOff : FiEye}
                          className="h-5 w-5 text-gray-400 hover:text-gray-600"
                        />
                      </button>
                    </div>
                    <p className="mt-2 text-sm text-gray-600">
                      Get your API key from{' '}
                      <a
                        href="https://platform.openai.com/api-keys"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-electric-600 hover:text-electric-700"
                      >
                        OpenAI Dashboard
                      </a>
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      OpenAI Model
                    </label>
                    <select
                      name="openai_model"
                      value={aiSettings.openai_model}
                      onChange={handleAiSettingsChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                    >
                      {openAIModels.map(model => (
                        <option key={model} value={model}>{model}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Straico settings - always show regardless of provider */}
                <div className="space-y-4 mt-6">
                  <h4 className="font-medium text-gray-700">Straico Settings</h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Straico API Key
                    </label>
                    <div className="relative">
                      <input
                        type={showStraicoApiKey ? 'text' : 'password'}
                        name="straico_api_key"
                        value={aiSettings.straico_api_key}
                        onChange={handleAiSettingsChange}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                        placeholder="Your Straico API key"
                      />
                      <button
                        type="button"
                        onClick={() => setShowStraicoApiKey(!showStraicoApiKey)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <SafeIcon
                          icon={showStraicoApiKey ? FiEyeOff : FiEye}
                          className="h-5 w-5 text-gray-400 hover:text-gray-600"
                        />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Straico Model Identifier
                    </label>
                    <input
                      type="text"
                      name="straico_model"
                      value={aiSettings.straico_model}
                      onChange={handleAiSettingsChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                      placeholder="YOUR_MODEL_IDENTIFIER"
                    />
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-between space-x-4 pt-6 border-t">
                {saveSuccess && (
                  <div className="flex items-center text-green-600">
                    <SafeIcon icon={FiCheck} className="h-5 w-5 mr-1" />
                    <span>Settings saved successfully!</span>
                  </div>
                )}
                {saveError && (
                  <div className="flex items-center text-red-600">
                    <span>{saveError}</span>
                  </div>
                )}
                <div className="flex justify-end space-x-4 ml-auto">
                  <button
                    onClick={onClose}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveSettings}
                    disabled={saving}
                    className="flex items-center space-x-2 px-6 py-2 bg-electric-600 text-white rounded-lg hover:bg-electric-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <SafeIcon icon={FiSave} className="h-4 w-4" />
                    <span>{saving ? 'Saving...' : 'Save Settings'}</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;